<script setup>
import DarkModeStore from '@o/store'

import { Card, Button } from '@components'
const { options, lastSavedOptions, saveChanges, showModal } = DarkModeStore()

const defaultOptions = {}
Object.keys(wp_dark_mode_admin_json.default).forEach(sectionKey => {
	const sectionValue = wp_dark_mode_admin_json.default[sectionKey]
	Object.keys(sectionValue).forEach(optionKey => {
		defaultOptions[sectionKey + '_' + optionKey] = sectionValue[optionKey].default
	})
})

const exportOptions = () => {

	// Adding validation
	lastSavedOptions.configuration = 'WPDarkMode'
	lastSavedOptions.version = wp_dark_mode_admin_json.version

	const data = JSON.stringify(lastSavedOptions, null, 2)

	const blob = new Blob([data], { type: 'application/json' })
	const a = document.createElement('a')
	a.href = URL.createObjectURL(blob)
	a.download = 'wp-dark-mode-settings-' + wp_dark_mode_admin_json.version + '.json'
	a.click()

}

const importOptions = (e) => {
	const file = e.target.files[0]

	// remove the file from the input
	e.target.value = ''

	const reader = new FileReader()
	reader.onload = (e) => {

		// Bail, if file is not JSON
		if (file.type !== 'application/json') {
			showModal({
				title: 'Invalid File',
				message: 'Please upload a valid file.',
				confirmText: 'Ok',
				confirmBackground: '#2563EB'
			})

			return
		}

		const data = JSON.parse(e.target.result)

		// Bail, if file is not JSON
		if (typeof data !== 'object') {
			showModal({
				title: 'Invalid File',
				message: 'Please upload a valid file.',
				confirmText: 'Ok',
				confirmBackground: '#2563EB'
			})

			return
		}

		// Validating the file
		if (data.configuration !== 'WPDarkMode') {
			showModal({
				title: 'Invalid File',
				message: 'Please upload a valid file.',
				confirmText: 'Ok',
				confirmBackground: '#2563EB'
			})

			return
		}

		const runImport = async () => {

			showModal({
				title: 'Are you sure want to import?',
				message: 'This will override all your current settings.',
				confirmText: 'Import',
				confirmBackground: 'blue',
				cancelText: 'Cancel',
				confirm: () => {
					setTimeout(async () => {
						const keys = Object.keys(data)
						keys.forEach(key => {
							options[key] = data[key]
						})

						await saveChanges()

						showModal({
							title: 'Import Settings',
							message: 'Settings imported successfully.',
							confirmText: 'Ok',
				confirmBackground: '#2563EB'
						})
					}, 100)
				},
			})


			
		}

		// Validating the version
		if (data.version !== wp_dark_mode_admin_json.version) {
			showModal({
				title: 'Version Mismatch',
				message: 'The version of the file you are trying to import is not compatible with the current version of WP Dark Mode.',
				confirmText: 'Import Anyway',
				cancelText: 'Cancel',
				confirm: runImport,
			})

			return
		}

		runImport()
	}
	reader.readAsText(file)
}


const resetOptions = () => {
	showModal({
		title: 'Reset Settings',
		message: 'Are you sure you want to reset all settings to default? We suggest you to export current settings before you reset the settings.',
		confirmText: 'Reset',
		cancelText: 'Cancel',
		confirm: () => {
			setTimeout(() => {
				
				showModal({
					title: 'Are you sure?',
					message: 'This action cannot be undone.',
					confirmText: 'Reset',
					cancelText: 'Cancel',
					confirm: async () => {
						const keys = Object.keys(defaultOptions)
						keys.forEach(key => {
							options[key] = defaultOptions[key]
						})
						await saveChanges()

						setTimeout(() => {
							showModal({
								title: 'Reset Settings',
								message: 'Settings reset successfully.',
								confirmText: 'Ok',
				confirmBackground: '#2563EB'
							})
						}, 100)
					},
				})
			}, 100)
		},
	})
}


</script>

<template>
	<section>
		<!-- utility tool  -->
		<Card borderless transparent class="w-full gap-8 max-w-xl [&>div]:w-full">
			<!-- Export settings  -->
			<div class="flex items-start  gap-5">
				<label class="text-sm font-medium whitespace-nowrap w-32">Export Settings</label>
				<div class="flex flex-col gap-2  w-full">
					<div><Button @click.prevent="exportOptions">
						<span class="dashicons dashicons-upload"></span>
						Export
					</Button></div>
					<span class="text-sm text-gray-500">Export current settings as JSON file.</span>
				</div>
			</div>
			<!-- Import settings  -->
			<div class="flex items-start  gap-5">
				<label class="text-sm font-medium whitespace-nowrap w-32">Import Settings</label>
				<div class="flex flex-col gap-2  w-full">
					<div><Button @click="$refs.importSettings.click()">
						<span class="dashicons dashicons-download"></span>
						<span>Import</span>
					</Button>
                    <input ref="importSettings" type="file" @input="importOptions" style="display:none"
                        accept="application/JSON"></div>
					<span class="text-sm text-gray-500">Import settings from JSON file which is exported through WP Dark Mode.</span>
				</div>
			</div>
			<!-- Reset settings  -->
			<div class="flex items-start  gap-5">
				<label class="text-sm font-medium whitespace-nowrap w-32">Reset Settings</label>
				<div class="flex flex-col gap-2 w-full">
					<div><Button color="red" @click.prevent="resetOptions">
						<span class="dashicons dashicons-image-rotate w-4"></span>
						<span>Reset Settings</span>
					</Button></div>
					<span class="text-sm text-gray-500">Reset all settings to default. We recommend to export current settings before
						you reset the settings.</span>
				</div>
			</div>
		</Card>
	</section>
</template>